import Vue from 'vue';

const BROWSER_INSTANCE_KEY = 'ezinterview-recording-browser-instance';
const pendingContexts = new Map();
const MESSAGE_REQUEST = 'ezinterview-recording-context-request';
const MESSAGE_RESPONSE = 'ezinterview-recording-context-response';
const MESSAGE_COMPLETED = 'ezinterview-recording-completed';
const MESSAGE_STOP = 'ezinterview-recording-stop';
const MESSAGE_TERMINAL_FAILURE = 'ezinterview-recording-terminal-failure';
const MESSAGE_STATUS = 'ezinterview-recording-status';
const FLOATING_PANEL_ID = 'ezinterview-recording-floating-panel';
const ACTIVE_RECORDING_KEY = 'ezinterview-active-recording-id';

const contextStorageKey = recordingId => `ezinterview-recording-context:${recordingId}`;

const clearRecordingLaunchContext = (recordingId) => {
  pendingContexts.delete(recordingId);
  sessionStorage.removeItem(contextStorageKey(recordingId));
  if (sessionStorage.getItem(ACTIVE_RECORDING_KEY) === recordingId) {
    sessionStorage.removeItem(ACTIVE_RECORDING_KEY);
  }
};

const persistRecordingLaunchContext = (context) => {
  sessionStorage.setItem(ACTIVE_RECORDING_KEY, context.recording_id);
  sessionStorage.setItem(contextStorageKey(context.recording_id), JSON.stringify(context));
};

const syncRecordingLocale = () => {
  const locales = { 'zh-hans': 'zh-Hans', 'zh-hant': 'zh-Hant', en: 'en' };
  localStorage.setItem('locale', locales[Vue.prototype._i18n.locale] || 'en');
};

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin) return;
  if (event.data?.type === MESSAGE_COMPLETED) {
    document.getElementById(FLOATING_PANEL_ID)?.remove();
    clearRecordingLaunchContext(String(event.data?.recordingId || ''));
    return;
  }
  if (event.data?.type === MESSAGE_TERMINAL_FAILURE) {
    const recordingId = String(event.data?.recordingId || '');
    const panel = document.getElementById(FLOATING_PANEL_ID);
    if (!panel || sessionStorage.getItem(ACTIVE_RECORDING_KEY) !== recordingId) return;
    panel.dataset.recordingFailed = 'true';
    if (panel.dataset.interviewEnded === 'true') {
      panel.remove();
      clearRecordingLaunchContext(recordingId);
    }
    return;
  }
  if (event.data?.type === MESSAGE_STATUS) {
    const recordingId = String(event.data?.recordingId || '');
    const panel = document.getElementById(FLOATING_PANEL_ID);
    if (!panel || sessionStorage.getItem(ACTIVE_RECORDING_KEY) !== recordingId) return;
    const failed = event.data?.failed === true;
    const status = panel.querySelector('[data-recording-role="collapsed-status"]');
    const elapsed = panel.querySelector('[data-recording-role="collapsed-elapsed"]');
    const dot = panel.querySelector('[data-recording-role="collapsed-dot"]');
    const waveform = panel.querySelector('[data-recording-role="collapsed-waveform"]');
    if (status) {
      status.textContent = String(event.data?.status || '');
      status.style.color = failed ? '#f04444' : '#1f2937';
      status.style.fontWeight = failed ? '700' : '500';
    }
    if (elapsed) elapsed.textContent = String(event.data?.elapsed || '00:00:00');
    if (waveform) waveform.style.display = failed ? 'none' : 'block';
    if (dot) {
      dot.style.width = failed ? '10px' : '7px';
      dot.style.height = failed ? '10px' : '7px';
      dot.style.boxShadow = failed ? '0 0 0 5px #ffe5e5' : 'none';
    }
    return;
  }
  if (event.data?.type !== MESSAGE_REQUEST) return;
  const recordingId = String(event.data?.recordingId || '');
  const context = pendingContexts.get(recordingId);
  if (context && event.source) {
    event.source.postMessage({ type: MESSAGE_RESPONSE, recordingId, context }, window.location.origin);
    pendingContexts.delete(recordingId);
  }
});

export const getBrowserInstanceId = () => {
  let value = localStorage.getItem(BROWSER_INSTANCE_KEY);
  if (!value) {
    value = `browser_${crypto.randomUUID().replace(/-/g, '')}`;
    localStorage.setItem(BROWSER_INSTANCE_KEY, value);
  }
  return value;
};

export const prepareRecordingWindow = () => {
  syncRecordingLocale();
};

export const cancelPreparedRecordingWindow = () => {};

export const launchRecordingWindow = (context) => {
  try {
    persistRecordingLaunchContext(context);
    pendingContexts.set(context.recording_id, context);
    const href = `/assistant/#/recording/${context.recording_id}`;
    document.getElementById(FLOATING_PANEL_ID)?.remove();

    const panel = document.createElement('section');
    panel.id = FLOATING_PANEL_ID;
    Object.assign(panel.style, {
      position: 'fixed',
      right: '16px',
      bottom: '16px',
      width: 'min(400px, calc(100vw - 24px))',
      height: 'min(500px, calc(100vh - 32px))',
      zIndex: '2147483000',
      background: '#fff',
      border: '1px solid #dfe5ee',
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(15,23,42,.18)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width .2s ease, height .2s ease, opacity .2s ease',
    });
    const header = document.createElement('header');
    Object.assign(header.style, {
      height: '58px',
      minHeight: '58px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      color: '#1f2937',
      background: '#fff',
      borderBottom: '1px solid #e9edf3',
      fontSize: '18px',
    });
    const titleWrap = document.createElement('span');
    Object.assign(titleWrap.style, { display: 'flex', alignItems: 'center', gap: '11px' });
    const titleIcon = document.createElement('span');
    titleIcon.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#2878ff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="8" y="3" width="8" height="12" rx="4"/><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3M9 21h6"/></svg>';
    Object.assign(titleIcon.style, { display: 'grid', width: '34px', height: '34px', placeItems: 'center', borderRadius: '8px', background: '#f1f6ff' });
    const title = document.createElement('strong');
    title.textContent = Vue.prototype._i18n.t('Arrangements.recording.title');
    titleWrap.append(titleIcon, title);
    const collapse = document.createElement('button');
    collapse.type = 'button';
    collapse.innerHTML = '<svg viewBox="0 0 20 14" width="20" height="14" fill="none" stroke="#8ba0bd" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 10 7-7 7 7"/></svg>';
    collapse.setAttribute('aria-label', Vue.prototype._i18n.t('Arrangements.recording.collapse'));
    Object.assign(collapse.style, {
      display: 'grid',
      width: '32px',
      height: '32px',
      placeItems: 'center',
      padding: '0',
      border: '0',
      background: 'transparent',
      cursor: 'pointer',
      pointerEvents: 'auto',
    });
    const frame = document.createElement('iframe');
    frame.src = href;
    frame.title = title.textContent;
    frame.allow = 'microphone; autoplay';
    Object.assign(frame.style, { width: '100%', flex: '1', border: '0', background: '#fff' });
    const collapsedStatus = document.createElement('div');
    Object.assign(collapsedStatus.style, {
      display: 'none',
      height: '46px',
      minHeight: '46px',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      background: '#fff',
      fontSize: '14px',
      pointerEvents: 'none',
    });
    const collapsedState = document.createElement('span');
    Object.assign(collapsedState.style, { display: 'flex', alignItems: 'center', gap: '8px' });
    const collapsedDot = document.createElement('i');
    collapsedDot.dataset.recordingRole = 'collapsed-dot';
    Object.assign(collapsedDot.style, {
      display: 'inline-block',
      width: '7px',
      height: '7px',
      flex: '0 0 auto',
      borderRadius: '50%',
      background: '#f04444',
    });
    const collapsedStateText = document.createElement('span');
    collapsedStateText.dataset.recordingRole = 'collapsed-status';
    const collapsedElapsed = document.createElement('strong');
    collapsedElapsed.dataset.recordingRole = 'collapsed-elapsed';
    collapsedElapsed.textContent = '00:00:00';
    Object.assign(collapsedElapsed.style, { color: '#334155', fontSize: '12px' });
    const collapsedDuration = document.createElement('span');
    Object.assign(collapsedDuration.style, { display: 'flex', alignItems: 'center', gap: '7px' });
    const collapsedWaveform = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    collapsedWaveform.dataset.recordingRole = 'collapsed-waveform';
    collapsedWaveform.setAttribute('viewBox', '0 0 28 24');
    collapsedWaveform.setAttribute('aria-hidden', 'true');
    collapsedWaveform.innerHTML = '<path d="M2 9v6"/><path d="M6 5v14"/><path d="M10 2v20"/><path d="M14 6v12"/><path d="M18 1v22"/><path d="M22 5v14"/><path d="M26 9v6"/>';
    Object.assign(collapsedWaveform.style, {
      width: '28px',
      height: '24px',
      fill: 'none',
      stroke: '#ff6b73',
      strokeLinecap: 'round',
      strokeWidth: '1.5',
    });
    collapsedDuration.append(collapsedWaveform, collapsedElapsed);
    collapsedState.append(collapsedDot, collapsedStateText);
    collapsedStatus.append(collapsedState, collapsedDuration);
    const collapsedAnimation = document.createElement('style');
    collapsedAnimation.textContent = `
      @keyframes ezinterview-recording-wave { from { opacity:.55;transform:scaleY(.35) } to { opacity:1;transform:scaleY(1) } }
      #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path { transform-box:fill-box;transform-origin:center;animation:ezinterview-recording-wave .75s ease-in-out infinite alternate }
      #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path:nth-child(2), #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path:nth-child(6) { animation-delay:-.45s }
      #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path:nth-child(3), #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path:nth-child(5) { animation-delay:-.25s }
      #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path:nth-child(4) { animation-delay:-.6s }
      @media (prefers-reduced-motion:reduce) { #${FLOATING_PANEL_ID} [data-recording-role="collapsed-waveform"] path { animation:none } }
    `;
    let collapsed = false;
    collapse.addEventListener('click', () => {
      collapsed = !collapsed;
      panel.style.width = collapsed ? 'min(360px, calc(100vw - 24px))' : 'min(400px, calc(100vw - 24px))';
      panel.style.height = collapsed ? '104px' : 'min(500px, calc(100vh - 32px))';
      panel.style.opacity = collapsed ? '.55' : '1';
      panel.style.pointerEvents = collapsed ? 'none' : 'auto';
      frame.style.display = collapsed ? 'none' : 'block';
      collapsedStatus.style.display = collapsed ? 'flex' : 'none';
      collapse.innerHTML = collapsed
        ? '<svg viewBox="0 0 20 14" width="20" height="14" fill="none" stroke="#8ba0bd" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 3 7 7 7-7"/></svg>'
        : '<svg viewBox="0 0 20 14" width="20" height="14" fill="none" stroke="#8ba0bd" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 10 7-7 7 7"/></svg>';
    });
    header.append(titleWrap, collapse);
    panel.append(collapsedAnimation, header, collapsedStatus, frame);
    document.body.appendChild(panel);
    return true;
  } catch {
    clearRecordingLaunchContext(context.recording_id);
    document.getElementById(FLOATING_PANEL_ID)?.remove();
    return false;
  }
};

export const notifyRecordingInterviewEnded = () => {
  const panel = document.getElementById(FLOATING_PANEL_ID);
  if (!panel) return;
  panel.dataset.interviewEnded = 'true';
  const recordingId = sessionStorage.getItem(ACTIVE_RECORDING_KEY) || '';
  if (panel.dataset.recordingFailed === 'true') {
    panel.remove();
    clearRecordingLaunchContext(recordingId);
    return;
  }
  const frame = panel.querySelector('iframe');
  frame?.contentWindow?.postMessage({ type: MESSAGE_STOP }, window.location.origin);
};

export const markRecordingLaunchFailed = (context) => {
  clearRecordingLaunchContext(context.recording_id);
  return fetch(`${context.api_base || '/interview/recording'}/${context.recording_id}/failed/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${context.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      error_code: 'recorder_panel_failed',
      error_message: 'Recorder panel could not be opened',
    }),
  });
};

const restoreActiveRecording = () => {
  if (window.parent !== window || document.getElementById(FLOATING_PANEL_ID)) return;
  const recordingId = sessionStorage.getItem(ACTIVE_RECORDING_KEY);
  if (!recordingId) return;
  try {
    const value = sessionStorage.getItem(contextStorageKey(recordingId));
    const restoredContext = value ? JSON.parse(value) : null;
    if (!restoredContext || restoredContext.recording_id !== recordingId) {
      clearRecordingLaunchContext(recordingId);
      return;
    }
    launchRecordingWindow(restoredContext);
  } catch {
    clearRecordingLaunchContext(recordingId);
  }
};

if (window.parent === window) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreActiveRecording, { once: true });
  } else {
    window.setTimeout(restoreActiveRecording, 0);
  }
}
